import React from 'react';
import { Button, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

type ErrorModalContextType = {
  /** Shows a blocking error modal. Multiple errors are queued and shown one by one. */
  showError: (message: React.ReactNode) => void;
};

const ErrorModalContext = React.createContext<ErrorModalContextType>({ showError: () => {} });

/**
 * Bridge for non-React modules (e.g. the API handler) to show the modal.
 * Registered by the provider below.
 */
let globalShowError: ((message: React.ReactNode) => void) | null = null;

export const showErrorModal = (message: React.ReactNode) => {
  if (globalShowError) {
    globalShowError(message);
  } else {
    // Provider not mounted yet (very early startup) - do not lose the error
    console.error('Error modal is not mounted yet:', message);
  }
};

/**
 * App-wide blocking error modal.
 *
 * Every backend request error (and the validation errors of the research project
 * flows) is shown here instead of a toast: it takes focus, cannot be dismissed by
 * clicking the mask, pressing ESC or the X, and stays open until the user clicks OK.
 */
export const ErrorModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = React.useState<React.ReactNode[]>([]);
  // 'forms.error.title' / 'buttons.ok' live in the local bundle as fallbacks
  const { t } = useTranslation(['translation', 'local']);

  const showError = React.useCallback((message: React.ReactNode) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  React.useEffect(() => {
    globalShowError = showError;
    return () => {
      globalShowError = null;
    };
  }, [showError]);

  const dismissCurrent = () => setMessages((prev) => prev.slice(1));

  return (
    <ErrorModalContext.Provider value={{ showError }}>
      {children}
      <Modal
        open={messages.length > 0}
        title={t('forms.error.title', 'Viga')}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        onCancel={dismissCurrent}
        footer={
          <Button type='primary' autoFocus onClick={dismissCurrent}>
            {t('buttons.ok', 'OK')}
          </Button>
        }
      >
        <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
          {messages[0]}
        </Typography.Paragraph>
        {messages.length > 1 && (
          <Typography.Text type='secondary' style={{ fontSize: '.85em' }}>
            {t('forms.error.moreErrors', '{{count}} more error(s) will follow.', { count: messages.length - 1 })}
          </Typography.Text>
        )}
      </Modal>
    </ErrorModalContext.Provider>
  );
};

export const useErrorModal = () => React.useContext(ErrorModalContext);

export default ErrorModalProvider;
